/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React from "react";

import { ProgressIndicator } from "@fluentui/react/lib/ProgressIndicator";

import { characterCounterStyles, CharacterCounterDiv } from "./CharacterCounter.styles";
import { ICharacterCounterProps } from "./ICharacterCounterProps";

/**
 * Character Counter.
 * @component
 * @param {ICharacterCounterProps} props The component props.
 */
const CharacterCounter: React.FunctionComponent<ICharacterCounterProps> = (
  props: ICharacterCounterProps
) => {
  const {
    currentCount,
    localizedCharactersDisplayText,
    localizedMaximumDisplayText,
    localizedMinimumDisplayText,
    maximumCount,
    minimumCount,
  } = props;
  const isMinimumCountMet = !!minimumCount && currentCount < minimumCount;
  const getMinimumCharactersText = (
    charactersText: string,
    minimumText?: string,
    minimum?: number
  ): string => `${minimumText} ${minimum} ${charactersText}`;

  const getMaximumCharactersText = (
    charactersText: string,
    maximumText?: string,
    maximum?: number
  ): string => `${maximumText} ${maximum} ${charactersText}`;

  const getCurrentMaximumCharactersText = (
    current: number,
    maximum: number,
    charactersText: string
  ): string => `${current} / ${maximum} ${charactersText}`;

  const getPercentValue = (current: number, maximum: number): number => current / maximum;

  return (
    <CharacterCounterDiv>
      <ProgressIndicator
        ariaValueText={"Percentage is " + getPercentValue(currentCount, maximumCount)}
        label={
          isMinimumCountMet
            ? getMinimumCharactersText(
                localizedCharactersDisplayText,
                localizedMinimumDisplayText,
                minimumCount
              )
            : getMaximumCharactersText(
                localizedCharactersDisplayText,
                localizedMaximumDisplayText,
                maximumCount
              )
        }
        description={getCurrentMaximumCharactersText(
          currentCount,
          maximumCount,
          localizedCharactersDisplayText
        )}
        barHeight={8}
        percentComplete={getPercentValue(currentCount, maximumCount)}
        styles={characterCounterStyles(isMinimumCountMet)}
      />
    </CharacterCounterDiv>
  );
};

export default CharacterCounter;
